import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ServiceManagerService } from '../service-manager.service';
import { HttpMethod } from '../../constants/constant';
import { HttpCustomOptions } from 'src/services/http-interceptor.service';

const SELF_CARE_SUBTYPES = [
  { label: 'Journal', value: 'self_care:journal' },
  { label: 'Movement', value: 'self_care:movement' },
  { label: 'Crisis', value: 'self_care:crisis' },
];

@Component({
  selector: 'app-resources',
  templateUrl: './resources.component.html',
  styleUrls: ['./resources.component.scss'],
})
export class ResourcesComponent implements OnInit {
  activeTab: 'self_care' | 'resource' | 'safety_plan' = 'self_care';

  resourceList: any[] = [];
  resourceForm!: FormGroup;
  isEditing = false;
  editingResourceId: string | null = null;
  resourceToDelete: any = null;

  selfCareSubtypes = SELF_CARE_SUBTYPES;

  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      ['link'],
      ['clean'],
    ],
  };

  constructor(
    private serviceManager: ServiceManagerService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadResources();
  }

  initForm(): void {
    this.resourceForm = this.fb.group({
      resource_name: ['', Validators.required],
      resource_body: [''],
      resource_content_url: [''],
      resource_type_name: ['', Validators.required],
      city: [''],
      issue_kind: [''],
      support_type: [''],
      payment: [''],
      is_queer_affirmative: [false],
      is_caste_affirmative: [false],
    });
  }

  selectTab(tab: 'self_care' | 'resource' | 'safety_plan'): void {
    this.activeTab = tab;
    this.loadResources();
  }

  loadResources(): void {
    this.serviceManager
      .CallBackendServiceAPI('admin-list-resources', { resource_type: this.activeTab })
      .subscribe((data: any) => {
        this.resourceList = data.resources || [];
      });
  }

  get isSelfCareTab(): boolean {
    return this.activeTab === 'self_care';
  }

  get isResourceTab(): boolean {
    return this.activeTab === 'resource';
  }

  resourceTypeLabelOf(typeName: string): string {
    const match = SELF_CARE_SUBTYPES.find((s) => s.value === typeName);
    return match ? match.label : typeName;
  }

  openCreateDialog(): void {
    this.isEditing = false;
    this.editingResourceId = null;
    this.resourceForm.reset({ is_queer_affirmative: false, is_caste_affirmative: false });

    if (!this.isSelfCareTab) {
      this.resourceForm.patchValue({ resource_type_name: this.activeTab });
    }

    document.getElementById('resourceDialogTrigger')?.click();
  }

  openEditDialog(resource: any): void {
    this.isEditing = true;
    this.editingResourceId = resource.resource_id;
    this.resourceForm.patchValue({
      resource_name: resource.resource_name,
      resource_body: resource.resource_body,
      resource_content_url: resource.resource_content_url,
      resource_type_name: resource.resource_type_name,
      city: resource.city,
      issue_kind: resource.issue_kind,
      support_type: resource.support_type,
      payment: resource.payment,
      is_queer_affirmative: resource.is_queer_affirmative,
      is_caste_affirmative: resource.is_caste_affirmative,
    });
    document.getElementById('resourceDialogTrigger')?.click();
  }

  saveResource(): void {
    if (this.resourceForm.invalid) return;

    const body: any = { ...this.resourceForm.value };

    const customOptions: HttpCustomOptions = {
      showErrorMessage: true,
      showSuccessMessage: true,
      createLoaderAction: true,
    };

    if (this.isEditing) {
      body['resource_id'] = this.editingResourceId;
      this.serviceManager
        .CallBackendServiceAPI('admin-update-resource', body, HttpMethod.POST, customOptions)
        .subscribe(() => {
          document.getElementById('resourceDialogClose')?.click();
          this.loadResources();
        });
    } else {
      this.serviceManager
        .CallBackendServiceAPI('admin-create-resource', body, HttpMethod.POST, customOptions)
        .subscribe(() => {
          document.getElementById('resourceDialogClose')?.click();
          this.loadResources();
        });
    }
  }

  confirmDelete(resource: any): void {
    this.resourceToDelete = resource;
    document.getElementById('resourceDeleteTrigger')?.click();
  }

  deleteResource(): void {
    if (!this.resourceToDelete) return;

    const customOptions: HttpCustomOptions = {
      showErrorMessage: true,
      showSuccessMessage: true,
      createLoaderAction: true,
    };

    this.serviceManager
      .CallBackendServiceAPI(
        'admin-delete-resource',
        { resource_id: this.resourceToDelete.resource_id },
        HttpMethod.POST,
        customOptions
      )
      .subscribe(() => {
        document.getElementById('resourceDeleteClose')?.click();
        this.resourceToDelete = null;
        this.loadResources();
      });
  }
}
